/**
 * Assistant de première connexion (first-run onboarding wizard).
 *
 * Un assistant en 3 étapes présenté une seule fois à un nouvel utilisateur :
 *   1. Bienvenue / confirmation de l’organisation.
 *   2. Inviter un coéquipier (réutilise lib/api/org — createOrgInvitation).
 *   3. Connecter une première plateforme (réutilise lib/api/platforms —
 *      listProviders + getAuthUrl).
 * À la fin (ou si l’utilisateur passe/ferme), `completeOnboarding()` est appelé
 * pour ne plus jamais le réafficher.
 *
 * Deux exports :
 *   - `OnboardingWizard` : l’assistant lui-même (overlay plein écran) ; prend un
 *     `onDone` appelé une fois l’onboarding marqué terminé.
 *   - `OnboardingGate` : composant autonome à monter dans le shell applicatif
 *     (à côté de <DemoTour /> dans DashboardLayout) ; il interroge le statut et
 *     n’affiche l’assistant que si l’onboarding est incomplet. Il échoue en mode
 *     OUVERT : la moindre erreur de statut ne bloque jamais l’application.
 */

import { useEffect, useState } from 'react';

import { getMyTenantId, getTenant } from '@/lib/api/tenant';
import {
  createOrgInvitation,
  ORG_ROLE_LABELS,
  type OrgRole,
} from '@/lib/api/org';
import { getAuthUrl, listProviders, type ProviderInfo } from '@/lib/api/platforms';
import { completeOnboarding, getOnboardingStatus } from '@/lib/api/onboarding';
import { isRateLimited } from '@/lib/api/client';
import { Badge, Button, ErrorBanner, Field, Select, Spinner, TextInput } from '@/routes/settings/ui';

/** Turn any thrown error into a user-facing French string. */
function errorMessage(err: unknown): string {
  if (isRateLimited(err)) {
    return 'Trop de requêtes — patientez un instant puis réessayez.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'Une erreur est survenue. Réessayez.';
}

const STEP_COUNT = 3;

// Org roles offered in the invite step — owner is intentionally excluded (the
// server restricts granting owner to owners; a new user rarely wants that).
const INVITE_ROLES: OrgRole[] = ['admin', 'member'];

// ---------------------------------------------------------------------------
// Step 1 — Welcome / confirm organisation
// ---------------------------------------------------------------------------

function WelcomeStep({ orgName }: { orgName: string | null }) {
  return (
    <div className="space-y-3">
      <div className="text-4xl" aria-hidden="true">
        👋
      </div>
      <h3 className="text-lg font-semibold text-foreground">Bienvenue !</h3>
      <p className="text-sm text-muted">
        Configurons rapidement votre espace{' '}
        {orgName ? (
          <>
            pour <span className="font-medium text-foreground">{orgName}</span>
          </>
        ) : (
          'de travail'
        )}
        . Trois étapes suffisent — vous pourrez passer chacune et y revenir plus tard.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Invite a teammate
// ---------------------------------------------------------------------------

function InviteStep() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [inviting, setInviting] = useState(false);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleInvite(event: React.FormEvent) {
    event.preventDefault();
    const target = email.trim();
    if (!target) return;
    setError(null);
    setInviting(true);
    try {
      await createOrgInvitation({ email: target, org_role: role });
      setInvitedEmail(target);
      setEmail('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setInviting(false);
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground">Invitez un coéquipier</h3>
      <p className="text-sm text-muted">
        Travaillez à plusieurs : envoyez une invitation par e-mail. C’est optionnel — vous pouvez
        passer cette étape.
      </p>

      {invitedEmail && (
        <div className="rounded-token border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">
          Invitation envoyée à <span className="font-medium">{invitedEmail}</span>.
        </div>
      )}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <form className="space-y-3" onSubmit={handleInvite}>
        <Field label="E-mail du coéquipier">
          <TextInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="prenom@entreprise.com"
            autoComplete="off"
          />
        </Field>
        <Field label="Rôle">
          <Select value={role} onChange={(e) => setRole(e.target.value as OrgRole)}>
            {INVITE_ROLES.map((r) => (
              <option key={r} value={r}>
                {ORG_ROLE_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" variant="secondary" disabled={inviting || !email.trim()}>
          {inviting ? 'Envoi…' : 'Envoyer l’invitation'}
        </Button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Connect a first platform
// ---------------------------------------------------------------------------

function ConnectStep() {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    listProviders()
      .then((list) => {
        if (alive) setProviders(list);
      })
      .catch((err) => {
        if (alive) setLoadError(errorMessage(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function handleConnect(platform: string) {
    setActionError(null);
    setConnecting(platform);
    try {
      const { auth_url } = await getAuthUrl(platform as ProviderInfo['platform']);
      // Open the provider OAuth in a new tab so the wizard keeps its state; the
      // callback returns to /platforms?status=success.
      window.open(auth_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setConnecting(null);
    }
  }

  const configured = providers.filter((p) => p.configured);

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-foreground">Connectez une plateforme</h3>
      <p className="text-sm text-muted">
        Reliez un premier réseau social pour publier et suivre vos performances. Optionnel — vous
        pourrez le faire depuis « Comptes connectés ».
      </p>

      {actionError && <ErrorBanner message={actionError} onDismiss={() => setActionError(null)} />}

      {loadError ? (
        <ErrorBanner message={loadError} onDismiss={() => setLoadError(null)} />
      ) : loading ? (
        <Spinner label="Chargement des plateformes…" />
      ) : configured.length === 0 ? (
        <p className="text-sm text-muted">
          Aucune plateforme n’est configurée pour le moment. Vous pourrez en connecter depuis
          l’onglet « Comptes connectés » des paramètres.
        </p>
      ) : (
        <ul className="space-y-2" aria-label="Plateformes connectables">
          {configured.map((p) => (
            <li
              key={p.platform}
              className="flex items-center justify-between gap-3 rounded-token border border-border bg-surface px-3 py-2"
            >
              <span className="flex items-center gap-2 text-sm">
                <span className="font-medium text-foreground">{p.label}</span>
                {p.connected && (
                  <Badge className="bg-success/15 text-success">Connecté</Badge>
                )}
              </span>
              <Button
                type="button"
                variant={p.connected ? 'ghost' : 'secondary'}
                onClick={() => handleConnect(p.platform)}
                disabled={connecting === p.platform}
              >
                {connecting === p.platform
                  ? 'Ouverture…'
                  : p.connected
                    ? 'Reconnecter'
                    : 'Connecter'}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The wizard (full-screen overlay)
// ---------------------------------------------------------------------------

export function OnboardingWizard({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState(0);
  const [orgName, setOrgName] = useState<string | null>(null);
  const [finishing, setFinishing] = useState(false);
  const [finishError, setFinishError] = useState<string | null>(null);

  // Best-effort org name for the welcome step (never blocks the wizard).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const id = await getMyTenantId();
        if (!id || !alive) return;
        const tenant = await getTenant(id);
        if (alive) setOrgName(tenant.name);
      } catch {
        /* welcome copy falls back to a generic phrasing */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /** Mark onboarding complete server-side, then hand control back to the shell.
   *  Fails open: even if the completion call errors, the wizard closes for this
   *  session so the user is never trapped. */
  async function finish() {
    setFinishError(null);
    setFinishing(true);
    try {
      await completeOnboarding();
      onDone();
    } catch (err) {
      // Surface the error but still let them out — the gate re-checks next load.
      setFinishError(errorMessage(err));
      onDone();
    } finally {
      setFinishing(false);
    }
  }

  const isLast = step === STEP_COUNT - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Assistant de première connexion"
    >
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-token-lg border border-border bg-surface shadow-elevation-3">
        {/* Header: progress + skip */}
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? 'w-6 bg-primary' : i < step ? 'w-3 bg-primary/50' : 'w-3 bg-surface-2'
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => void finish()}
            disabled={finishing}
            className="rounded-token px-2 py-1 text-xs text-muted transition-colors hover:bg-surface-2 hover:text-foreground disabled:opacity-50"
          >
            Passer
          </button>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {step === 0 && <WelcomeStep orgName={orgName} />}
          {step === 1 && <InviteStep />}
          {step === 2 && <ConnectStep />}
          {finishError && (
            <div className="mt-4">
              <ErrorBanner message={finishError} onDismiss={() => setFinishError(null)} />
            </div>
          )}
        </div>

        {/* Footer: navigation */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-5 py-3">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || finishing}
          >
            Précédent
          </Button>
          {isLast ? (
            <Button type="button" variant="primary" onClick={() => void finish()} disabled={finishing}>
              {finishing ? 'Finalisation…' : 'Terminer'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              onClick={() => setStep((s) => Math.min(STEP_COUNT - 1, s + 1))}
            >
              Suivant
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The gate (mount in the app shell)
// ---------------------------------------------------------------------------

/**
 * Self-contained first-run gate. Fetches the caller's onboarding status once and
 * renders the wizard only if it is incomplete. Fails OPEN: any error (400/404/
 * network/rate-limit) is treated as "don't show the wizard" so the app is never
 * blocked. Drop `<OnboardingGate />` into the app shell (see DashboardLayout,
 * next to <DemoTour />).
 */
export function OnboardingGate() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let alive = true;
    getOnboardingStatus()
      .then((status) => {
        if (alive && status && status.completed === false) setShow(true);
      })
      .catch(() => {
        /* fail open — never block the app on a status hiccup */
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!show) return null;
  return <OnboardingWizard onDone={() => setShow(false)} />;
}
